import "react-datepicker/dist/react-datepicker.css";
import { ChangeEvent, useEffect, useState } from "react";
// @ts-expect-error: ignoring as it is existing js file
import DatePicker from "react-datepicker";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, FormHelperText, InputAdornment, TextField, Typography } from "@mui/material";
import { Button, IconButton, Input, Sheet, Stack, useFinanceableTheme } from "@financeable-com-au/financeable-ui";
import moment from "moment";
import { ReportExportPayload } from "@/utils/interface";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";

interface DateRangeExportPanelProps {
  title: string;
  description: string;
  loading: boolean;
  onExport: (payload: ReportExportPayload) => Promise<boolean | void> | boolean | void;
  defaultEmail?: string;
  exportButtonLabel?: string;
  exportingButtonLabel?: string;
}

const getEmailValidationMessage = (email: string) => {
  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    return "Enter an email address.";
  }

  if (!regex.email.test(trimmedEmail.toLowerCase())) {
    return "Enter a valid email address.";
  }

  return "";
};

const DateRangeExportPanel = ({
  title,
  description,
  loading,
  onExport,
  defaultEmail = "",
  exportButtonLabel = "Send Report",
  exportingButtonLabel = "Sending Report...",
}: DateRangeExportPanelProps) => {
  const theme = useFinanceableTheme();
  const normalisedDefaultEmail = defaultEmail.trim();
  const [filterDate, setFilterDate] = useState<{ fromDate: Date | null; toDate: Date | null }>({
    fromDate: null,
    toDate: null,
  });
  const [email, setEmail] = useState(normalisedDefaultEmail);
  const [hasEditedEmail, setHasEditedEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const hasSelectedDateRange = Boolean(filterDate.fromDate && filterDate.toDate);
  const selectedDateRange = hasSelectedDateRange
    ? `${moment(filterDate.fromDate).format("DD MMM YYYY")} - ${moment(filterDate.toDate).format("DD MMM YYYY")}`
    : "Select a start and end date to send the report.";
  const emailValidationMessage = getEmailValidationMessage(email);
  const showEmailError = Boolean(emailError || (email.trim() && emailValidationMessage));
  const isSendDisabled = loading || !hasSelectedDateRange;
  const dateStatusStyles = hasSelectedDateRange
    ? {
        backgroundColor: "#ECFDF3",
        borderColor: "#12B76A",
        color: "#027A48",
      }
    : {
        backgroundColor: "#FFE266",
        borderColor: "#D99A00",
        color: theme.palette.text.primary,
      };
  const fieldLabelStyles = {
    display: "block",
    mb: 0.75,
    color: theme.palette.text.primary,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "16px",
  };
  const fieldRootStyles = {
    minHeight: 44,
    borderColor: theme.palette.neutral[300],
    borderRadius: "8px",
    backgroundColor: theme.palette.background.popup,
    "&:hover": {
      borderColor: theme.palette.primary[300],
    },
    "&.Mui-focused, &.Mui-focusVisible": {
      borderColor: theme.palette.primary[500],
      boxShadow: `0 0 0 2px ${theme.palette.primary[100]}`,
    },
  };
  const dateFieldStyles = {
    "& .MuiOutlinedInput-root": {
      ...fieldRootStyles,
      paddingRight: 1,
      "& fieldset": {
        borderColor: theme.palette.neutral[300],
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary[300],
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary[500],
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input": {
      py: 1.25,
      color: theme.palette.text.primary,
    },
  };
  const datePickerStyles = {
    "& .react-datepicker-wrapper": { width: "100%" },
    "& .react-datepicker__input-container": { width: "100%" },
    "& .react-datepicker-popper": {
      zIndex: 20,
    },
  };

  useEffect(() => {
    if (!hasEditedEmail) {
      setEmail(normalisedDefaultEmail);
      setEmailError(normalisedDefaultEmail ? getEmailValidationMessage(normalisedDefaultEmail) : "");
    }
  }, [hasEditedEmail, normalisedDefaultEmail]);

  const handleDateChange = (dates: (Date | null)[]) => {
    setFilterDate({ fromDate: dates?.[0] ?? null, toDate: dates?.[1] ?? null });
    setSubmittedEmail("");
  };

  const handleClearDateRange = () => {
    setFilterDate({ fromDate: null, toDate: null });
    setSubmittedEmail("");
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextEmail = event.target.value;
    setHasEditedEmail(true);
    setEmail(nextEmail);
    setSubmittedEmail("");

    if (emailError) {
      setEmailError(getEmailValidationMessage(nextEmail));
    }
  };

  const handleEmailBlur = () => {
    setEmailError(getEmailValidationMessage(email));
  };

  const handleExport = async () => {
    if (!filterDate.fromDate || !filterDate.toDate) {
      return;
    }

    if (emailValidationMessage) {
      setEmailError(emailValidationMessage);
      return;
    }

    const recipientEmail = email.trim();
    setSubmittedEmail(recipientEmail);

    const result = await onExport({
      fromDate: filterDate.fromDate,
      toDate: filterDate.toDate,
      email: recipientEmail,
    });

    if (result === false) {
      setSubmittedEmail("");
    }
  };

  return (
    <Sheet
      variant="outlined"
      borderRadius="md"
      borderWidth="sm"
      sx={{
        width: "100%",
        mt: 2,
        p: { xs: 2, sm: 3 },
        borderColor: theme.palette.neutral[300],
        bgcolor: theme.palette.background.popup,
      }}
    >
      <Stack rowGap={3}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: "#F4F3FF",
                border: "1px solid",
                borderColor: "#B692F6",
                color: "#475467",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CalendarMonthOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ color: theme.palette.text.primary, fontSize: 20, fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: 14,
                  lineHeight: "21px",
                  mt: 0.5,
                  maxWidth: 620,
                }}
              >
                {description}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignSelf: { xs: "stretch", md: "flex-start" },
              justifyContent: { xs: "flex-start", md: "flex-end" },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: "2px",
                border: "1px solid",
                boxShadow: `inset 0 0 0 1px ${hasSelectedDateRange ? "#12B76A" : "#D99A00"}`,
                whiteSpace: "nowrap",
                ...dateStatusStyles,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: "16px" }}>
                {hasSelectedDateRange ? selectedDateRange : "Date range required"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(320px, 420px) minmax(300px, 340px) 180px",
            },
            gap: { xs: 2, md: 1 },
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography component="label" htmlFor="report-date-range" sx={fieldLabelStyles}>
              Report date range
            </Typography>
            <Box
              sx={{
                width: "100%",
                ...datePickerStyles,
              }}
            >
              <DatePicker
                selected={filterDate?.fromDate}
                onChange={handleDateChange}
                startDate={filterDate?.fromDate}
                endDate={filterDate?.toDate}
                selectsRange
                maxDate={new Date()}
                name="report-date-range"
                monthsShown={2}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <TextField
                    id="report-date-range"
                    fullWidth
                    placeholder="Select start and end date"
                    sx={dateFieldStyles}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.icon }} />
                        </InputAdornment>
                      ),
                      endAdornment: hasSelectedDateRange ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear report date range"
                            size="sm"
                            variant="inline"
                            intent="neutral"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleClearDateRange();
                            }}
                          >
                            <CancelRoundedIcon sx={{ color: theme.palette.primary[500], fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                }
              />
            </Box>
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12, lineHeight: "16px", mt: 0.75 }}>
              {selectedDateRange}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography component="label" htmlFor="report-email-recipient" sx={fieldLabelStyles}>
              Email recipient{" "}
              <Box component="span" sx={{ color: theme.palette.danger[500] }}>
                *
              </Box>
            </Typography>
            <Box
              sx={{
                "& .MuiInput-root": fieldRootStyles,
              }}
            >
              <Input
                id="report-email-recipient"
                fullWidth
                size="lg"
                type="email"
                value={email}
                placeholder="name@example.com"
                startDecorator={<EmailOutlinedIcon sx={{ fontSize: "1rem", color: theme.palette.text.icon }} />}
                intent={showEmailError ? "danger" : undefined}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
              />
            </Box>
            {showEmailError ? (
              <FormHelperText error sx={{ alignItems: "center", display: "flex", gap: 0.5, mt: 0.75 }}>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                {emailError || emailValidationMessage}
              </FormHelperText>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "flex",
              pt: { xs: 0, md: "22px" },
              "& .MuiButton-root": {
                height: 44,
                minHeight: 44,
                width: "100%",
              },
            }}
          >
            <Button variant="primary" size="lg" disabled={isSendDisabled} loading={loading} onClick={handleExport}>
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                <SendOutlinedIcon sx={{ fontSize: 20 }} />
                {loading ? exportingButtonLabel : exportButtonLabel}
              </Box>
            </Button>
          </Box>
        </Box>

        {submittedEmail ? (
          <Stack
            direction="row"
            columnGap={1}
            alignItems="flex-start"
            sx={{
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.primary[50],
              border: "1px solid",
              borderColor: theme.palette.primary[100],
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary[500], mt: 0.25 }} />
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14, lineHeight: "20px" }}>
              Your report is being prepared. This can take a few minutes, and we will email it to {submittedEmail} as
              soon as it is ready.
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Sheet>
  );
};

export default DateRangeExportPanel;
